import {
  GetScriptStatusResponseBodyType,
  ScriptStatusMode,
} from "@intelligems/ig-types/functions/shops/get-script-status";
import { QueryOptions } from "@src/types/react-query";
import { QueryClient, useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { organizationScriptStatusKey } from "../queryKeys";

const getScriptStatus = async (
  themeId?: string,
  fullUrl?: string,
  mode?: ScriptStatusMode
) => {
  const resp = await api.get<GetScriptStatusResponseBodyType>(
    "/v2/organizations/script-status",
    {
      params: {
        themeId,
        fullUrl,
        mode,
      },
    }
  );
  return resp.data;
};

const failedRequests = new Set<string>();

export const clearFailedScriptStatusRequests = (orgId: string) => {
  const keysToDelete = Array.from(failedRequests).filter((key) =>
    key.startsWith(`${orgId}-`)
  );
  keysToDelete.forEach((key) => failedRequests.delete(key));
};

/**
 * Clears failed script status requests and invalidates the query cache for a given org.
 * Use this when retesting script installation to allow fresh requests.
 *
 * @param queryClient - The React Query client instance
 * @param orgId - Organization ID to refresh. No-op if undefined.
 * @param options - Optional query invalidation options (e.g., { exact: false })
 */
export const refreshScriptStatus = (
  queryClient: QueryClient,
  orgId: string | undefined,
  options?: { exact?: boolean; mode: "quick" | "full" }
) => {
  if (!orgId) {
    return;
  }
  const isQuickMode = options?.mode === "quick";
  clearFailedScriptStatusRequests(orgId);

  if (isQuickMode) {
    void queryClient.invalidateQueries({
      queryKey: organizationScriptStatusKey.preview(
        orgId,
        undefined,
        undefined,
        "quick"
      ),
      ...options,
    });
  } else {
    void queryClient.invalidateQueries({
      queryKey: organizationScriptStatusKey.org(orgId),
      ...options,
    });
  }
};

export const useGetScriptStatus = (
  orgId?: string,
  themeId?: string,
  fullUrl?: string,
  mode: ScriptStatusMode = "full",
  options?: QueryOptions<GetScriptStatusResponseBodyType, Error>
) => {
  const errorKey = `${orgId}-${themeId}-${fullUrl}-${mode}`;
  const queryKey = organizationScriptStatusKey.preview(
    orgId!,
    themeId,
    fullUrl,
    mode
  );
  return useQuery<GetScriptStatusResponseBodyType, Error>({
    queryKey,
    queryFn: async () => {
      const res = await getScriptStatus(themeId, fullUrl, mode);
      failedRequests.delete(errorKey); // Clear on success
      return res;
    },
    enabled: !failedRequests.has(errorKey) && !!orgId,
    retry: 1,
    ...options,
    meta: {
      onError: {
        any: () => failedRequests.add(errorKey),
      },
    },
  });
};
